import { DashboardComponent } from '@platform/dashboard/pages/dashboard/dashboard.component';
import { PreloadCheckpoints } from '@shared/enums/preload-checkpoints.enum';
import { ErrorGuard } from '@shared/guards/error.guard';
import { LandlordGuard } from '@shared/guards/landlord-guard.service';
import { CustomRoute } from '@shared/models/custom-route.model';
import { CompanyClientUiSettingsService } from '@shared/services/company-client-ui-settings.service';
import { DialogService } from 'primeng/dynamicdialog';
import { paths } from './paths';
import { PlatformComponent } from './platform.component';

export const platformRoutes: CustomRoute[] = [
  {
    path: '',
    component: PlatformComponent,
    providers: [DialogService],
    children: [
      // dashboard
      { path: '', pathMatch: 'full', redirectTo: paths.platform.DASHBOARD },
      {
        title: 'Dashboard',
        path: paths.platform.DASHBOARD,
        loadComponent: async () => (await import('@app/platform/dashboard/pages/dashboard/dashboard.component')).DashboardComponent,
        data: { preloadCheckpoint: PreloadCheckpoints.PLATFORM, animation: 0 },
      },
      {
        path: paths.platform.FIRST_TIME,
        component: DashboardComponent,
        data: { preloadCheckpoint: PreloadCheckpoints.PLATFORM },
      },
      // portfolio-landlord
      {
        title: 'Portfolio',
        path: paths.platform.PROPERTIES,
        canMatch: [LandlordGuard],
        loadComponent: async () =>
          (await import('@platform/portfolio/pages/portfolio-list/portfolio-list.component')).PortfolioListComponent,
      },
      // portfolio-default
      {
        title: 'Portfolio',
        path: paths.platform.PROPERTIES,
        loadComponent: async () =>
          (await import('@platform/portfolio/pages/portfolio-carousel/portfolio-carousel.component')).PortfolioCarouselComponent,
      },
      // portfolio-list
      {
        title: 'Portfolio Card',
        path: `${paths.platform.PROPERTY_CARD}/:propertyId`,
        loadComponent: async () =>
          (await import('@platform/portfolio/pages/portfolio-card-detail/portfolio-card-detail.component')).PortfolioCardDetailComponent,
      },
      // portfolio-list
      {
        title: 'Smart Search Card',
        path: `${paths.platform.SMART_SEARCH_CARD}/:smartSearchId`,
        canMatch: [CompanyClientUiSettingsService],
        loadComponent: async () =>
          (await import('@platform/portfolio/pages/portfolio-card-detail/portfolio-card-detail.component')).PortfolioCardDetailComponent,
        data: {
          companyUISettings: 'showSmartSearch',
        },
      },
      // property
      {
        title: 'Add Property',
        path: `${paths.platform.PROPERTY}/${paths.platform.property.ADD}`,
        loadComponent: async () =>
          (await import('@platform/property/pages/add-new-property/add-new-property.component')).AddNewPropertyComponent,
      },
      {
        title: 'Property Detail',
        path: `${paths.platform.PROPERTY}/:propertyId`,
        loadComponent: async () => (await import('@platform/property-detail/property-detail.component')).PropertyDetailComponent,
      },
      // epc
      {
        title: 'EPC',
        path: `${paths.platform.EPC}/:propertyId`,
        loadComponent: async () => (await import('@platform/epc/pages/epc/epc.component')).EpcComponent,
      },
      // mortgage
      {
        title: 'Mortgage Detail',
        path: `${paths.platform.MORTGAGE_DETAIL}/:mortgageId`,
        loadComponent: async () => (await import('@platform/mortgage-detail/mortgage-detail.component')).MortgageDetailComponent,
      },
      {
        title: 'Add Mortgage',
        path: `${paths.platform.MORTGAGES}/${paths.platform.mortgages.ADD}`,
        loadComponent: async () => (await import('@platform/add-new-mortgage/add-new-mortgage.component')).AddNewMortgageComponent,
      },
      // mortgage case
      {
        title: 'Mortgage Case Detail',
        path: `${paths.platform.MORTGAGE_CASE_DETAIL}/:mortgageCaseId`,
        loadComponent: async () =>
          (await import('@platform/case/pages/mortgage-case-detail/mortgage-case-detail.component')).MortgageCaseDetailComponent,
      },
      // documents
      {
        title: 'Documents',
        path: paths.platform.DOCUMENTS,
        canMatch: [CompanyClientUiSettingsService],
        loadComponent: async () => (await import('@platform/documents/pages/documents/documents.component')).DocumentsComponent,
        data: {
          companyUISettings: 'showDocument',
          preloadCheckpoint: PreloadCheckpoints.PLATFORM,
        },
      },
      // MAB articles
      {
        title: 'MAB articles',
        path: paths.platform.MAB_ARTICLES,
        loadComponent: async () => (await import('@platform/articles/mab/mab-articles.component')).MabArticlesComponent,
        loadChildren: () => import('@platform/articles/mab/mab-articles.routes').then((r) => r.mabArticlesRoutes),
      },
      // profile
      {
        title: 'Profile',
        path: `${paths.platform.PROFILE}`,
        loadComponent: async () => (await import('@platform/profile/pages/profile/profile.component')).ProfileComponent,
      },
      {
        title: 'Profile - Email & Notifications',
        path: `${paths.platform.PROFILE}/${paths.platform.profile.NOTIFICATIONS_SETTING}`,
        loadComponent: async () =>
          (await import('@platform/profile/pages/email-notifications/email-notifications.component')).EmailNotificationsComponent,
      },
      {
        title: 'Profile - Comparison Preferences',
        path: `${paths.platform.PROFILE}/${paths.platform.profile.SEARCH_PREFERENCES}`,
        loadComponent: async () =>
          (await import('@platform/profile/pages/search-preferences/search-preferences.component')).SearchPreferencesComponent,
      },
      {
        title: 'Profile - Credit & Expenditure',
        path: `${paths.platform.PROFILE}/${paths.platform.profile.CREDIT_REPORT}`,
        loadComponent: async () => (await import('@platform/profile/pages/credit/credit.component')).CreditComponent,
      },
      {
        title: 'Profile - Income & Affordability',
        path: `${paths.platform.PROFILE}/${paths.platform.profile.INCOME}`,
        loadComponent: async () => (await import('@platform/profile/pages/income/income.component')).IncomeComponent,
      },
      {
        title: 'Profile - Your Adviser',
        path: `${paths.platform.PROFILE}/${paths.platform.profile.ADVISOR}`,
        loadComponent: async () =>
          (await import('@platform/profile/pages/profile-mortgage-adviser/profile-mortgage-adviser.component'))
            .ProfileMortgageAdviserComponent,
      },
      {
        title: 'Profile - Actions',
        path: `${paths.platform.PROFILE}/${paths.platform.profile.ACTIONS}`,
        loadComponent: async () =>
          (await import('@platform/profile/pages/profile-actions/profile-actions.component')).ProfileActionsComponent,
      },
      // smart search
      {
        title: 'Smart Search',
        path: `${paths.platform.SMART_SEARCH}/:id`,
        canMatch: [CompanyClientUiSettingsService],
        loadComponent: async () =>
          (await import('@platform/smart-search/pages/smart-search-detail/smart-search-detail.component')).SmartSearchDetailComponent,
        data: {
          companyUISettings: 'showSmartSearch',
          animation: 1,
        },
      },
      //better deal
      {
        title: 'Better Deal Detail',
        path: `${paths.platform.OPPORTUNITY_BETTER_DEAL_DETAIL}/:id`,
        loadComponent: async () =>
          (await import('@platform/property/pages/opportunity-better-deal-detail/opportunity-better-deal-detail.component'))
            .OpportunityBetterDealDetailComponent,
      },
      {
        title: 'Better deal view',
        path: `${paths.platform.BETTER_DEAL_COMPARISON}/:id`,
        loadComponent: async () =>
          (await import('@platform/better-deal-comparison/better-deal-comparison.component')).BetterDealComparisonComponent,
      },
      // Remortgage Guide
      {
        title: 'Remortgage Guide',
        path: `${paths.platform.REMORTGAGE_GUIDE}`,
        loadComponent: async () => (await import('@platform/remortgage-guide/remortgage-guide.component')).RemortgageGuideComponent,
      },
      // help case
      {
        title: 'Help case detail',
        path: `${paths.platform.HELP_CASE}/:helpCaseId`,
        loadComponent: async () =>
          (await import('@platform/help-case/pages/help-case-detail/help-case-detail.component')).HelpCaseDetailComponent,
      },
      {
        title: 'Error',
        path: paths.platform.ERROR,
        loadChildren: () => import('@platform/OLD_error/error.module').then((m) => m.ErrorModule),
        canActivate: [ErrorGuard],
        data: {
          preloadCheckpoint: PreloadCheckpoints.PLATFORM,
          title: 'Error',
        },
      },
    ],
  },
];
